/**
 * External Layers Action Component
 * Combined component for WMS, ABB Weather, Vector Tile, and Radar layers
 * with folder-like navigation structure
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  Cloud,
  Globe,
  Grid3X3,
  Search,
  Circle,
  Anchor,
  Radio,
  MoreVertical,
  Loader2,
} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icons,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TruncatedText,
  useToast,
  SearchInput,
} from '@ais-ui/ui-core';
import { useLayers } from '@ais-ui/react-openlayers';
import { useLocalization } from '@ais-ui/locale-management';
import TileLayer from 'ol/layer/Tile';
import { XYZ } from 'ol/source';

import { VectorTile, WMSInfo, WMSLayerInfo } from '@ais-ui/websockets';
import { useSettings } from '../../../Settings/SettingsProvider';
import { useVectorTileSettings } from '../../../VectorTiles/useVectorTileSettings';
import {
  useWMSSettings,
  useWmsLayerActions,
} from '../../../WMSLayers/useWMSSettings';
import { cn } from '@ais-ui/utils';
import {
  PiStar,
  PiStarFill,
  PiEye,
  PiEyeClosed,
  PiStackThin,
} from 'react-icons/pi';
import { useLayerFavorites, LAYER_IDS } from '../../onMapStartup/LayersOnStart';
import { useRadarSettings } from '../../../RadarOverlay/useRadarSettings';
import { usePermissions } from '../../../Permissions/PermissionsProvider';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../../../routes';
import {
  useABBLayers,
  useABBLayerActions,
  getABBMapLayerId,
} from './useABBWeatherLayers';

// Types for navigation
type LayerSourceType = 'wms' | 'vectortile' | 'radar' | 'abb';

interface NavigationState {
  sourceType: LayerSourceType | null;
  groupId: string | null;
  breadcrumbs: {
    id: string | null;
    label: string;
    sourceType?: LayerSourceType;
  }[];
}

export const ExternalLayers: React.FC = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();
  const permissions = usePermissions();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { addLayer, removeLayer, getLayerById, doesLayerExist, addVectorTile } =
    useLayers();
  const layerFavorites = useLayerFavorites();

  // Navigation state - start at root (showing all groups)
  const [navigation, setNavigation] = useState<NavigationState>({
    sourceType: null,
    groupId: null,
    breadcrumbs: [{ id: null, label: 'External Layers' }],
  });

  // Reset navigation and search when popover closes
  useEffect(() => {
    if (!open) {
      setNavigation({
        sourceType: null,
        groupId: null,
        breadcrumbs: [{ id: null, label: 'External Layers' }],
      });
      setSearchQuery('');
    }
  }, [open]);

  // ==================== WMS ====================
  const wmsSettings = useWMSSettings();
  const { toggleWmsLayer, doesLayerExist: wmsDoesLayerExist } =
    useWmsLayerActions();

  // ==================== Vector Tiles ====================
  const vectorTiles = useVectorTileSettings();

  // ==================== Radar Overlays ====================
  const { radarSources, toggleRadarLayer } = useRadarSettings();

  // ==================== ABB Weather ====================
  const {
    layers: abbLayers,
    groups: abbGroups,
    isLoading: isAbbLoading,
  } = useABBLayers();
  const { toggleLayer: toggleAbbLayer, loadingLayerId: abbLoadingLayerId } =
    useABBLayerActions();

  // ==================== OpenSeaMap ====================
  const OPENSEAMAP_LAYER_ID = 'openseamap-seamark';
  const OPENSEAMAP_URL = 'https://t1.openseamap.org/seamark/{z}/{x}/{y}.png';

  const handleOpenSeaMapToggle = useCallback(() => {
    if (doesLayerExist(OPENSEAMAP_LAYER_ID)) {
      const existingLayer = getLayerById(OPENSEAMAP_LAYER_ID);
      if (existingLayer) {
        removeLayer(existingLayer.layer);
      }
    } else {
      const source = new XYZ({
        url: OPENSEAMAP_URL,
        crossOrigin: 'anonymous',
        attributions: '© OpenSeaMap contributors',
      });
      const tileLayer = new TileLayer({ source });
      addLayer(
        tileLayer,
        'OpenSeaMap',
        true,
        false,
        false,
        { type: 'openseamap' },
        OPENSEAMAP_LAYER_ID,
        false,
        false,
        false,
        'below-ships',
      );
    }
  }, [doesLayerExist, getLayerById, removeLayer, addLayer]);

  // ==================== Active Layer Tracking ====================

  // Check if any WMS layer in a service is active
  const getWmsServiceActiveCount = useCallback(
    (wmsInfo: WMSInfo) => {
      return (
        wmsInfo.layers?.filter((layer) => doesLayerExist(layer.id)).length || 0
      );
    },
    [doesLayerExist],
  );

  // Check if any vector tile is active
  const getVectorTileActiveCount = useCallback(() => {
    return vectorTiles.filter((vt) => doesLayerExist(vt.id)).length;
  }, [vectorTiles, doesLayerExist]);

  // Check if any ABB layer in a group is active
  const getAbbGroupActiveCount = useCallback(
    (groupId: string) => {
      return abbLayers
        .filter((l) => l.group === groupId)
        .filter((l) => doesLayerExist(getABBMapLayerId(l.title))).length;
    },
    [abbLayers, doesLayerExist],
  );

  // ==================== Layer Selection Handlers ====================

  const handleWmsLayerSelect = useCallback(
    async (wmsInfo: WMSInfo, wmsLayerInfo: WMSLayerInfo) => {
      await toggleWmsLayer(wmsInfo, wmsLayerInfo);
    },
    [toggleWmsLayer],
  );

  const handleVectorTileSelect = useCallback(
    (vectorTile: VectorTile) => {
      const enable = !doesLayerExist(vectorTile.id);

      if (enable) {
        addVectorTile(vectorTile, true, vectorTile.id);
      } else {
        const layerItem = getLayerById(vectorTile.id);
        if (layerItem) removeLayer(layerItem.layer);
      }

      toast({
        title: messages.genericToastTitleSuccess,
        description: enable
          ? `Added vector tile: ${vectorTile.name}`
          : `Removed vector tile: ${vectorTile.name}`,
      });
    },
    [doesLayerExist, addVectorTile, getLayerById, removeLayer, toast, messages],
  );

  // ==================== Navigation ====================

  const navigateToGroup = useCallback(
    (groupId: string, label: string, sourceType: LayerSourceType) => {
      setNavigation((prev) => ({
        sourceType,
        groupId,
        breadcrumbs: [...prev.breadcrumbs, { id: groupId, label, sourceType }],
      }));
      setSearchQuery('');
    },
    [],
  );

  const navigateBack = useCallback(() => {
    setNavigation((prev) => {
      if (prev.breadcrumbs.length <= 1) return prev;
      const newBreadcrumbs = prev.breadcrumbs.slice(0, -1);
      const lastCrumb = newBreadcrumbs[newBreadcrumbs.length - 1];

      if (newBreadcrumbs.length === 1) {
        return { sourceType: null, groupId: null, breadcrumbs: newBreadcrumbs };
      }
      return {
        sourceType: lastCrumb.sourceType || null,
        groupId: lastCrumb.id,
        breadcrumbs: newBreadcrumbs,
      };
    });
    setSearchQuery('');
  }, []);

  // ==================== Search & Filter ====================

  const normalizedQuery = searchQuery.toLowerCase().trim();

  // Filter WMS services and layers by search
  const filteredWmsServices = useMemo(() => {
    if (!wmsSettings?.wmsInfos) return [];
    if (!normalizedQuery) return wmsSettings.wmsInfos;

    return wmsSettings.wmsInfos
      .map((wms) => ({
        ...wms,
        layers: wms.layers?.filter(
          (layer) =>
            layer.title.toLowerCase().includes(normalizedQuery) ||
            wms.name.toLowerCase().includes(normalizedQuery),
        ),
      }))
      .filter(
        (wms) =>
          wms.name.toLowerCase().includes(normalizedQuery) ||
          (wms.layers && wms.layers.length > 0),
      );
  }, [wmsSettings?.wmsInfos, normalizedQuery]);

  // Filter vector tiles by search
  const filteredSearchVectorTiles = useMemo(() => {
    if (!vectorTiles) return [];
    if (!normalizedQuery) return vectorTiles;
    return vectorTiles.filter((vt) =>
      vt.name.toLowerCase().includes(normalizedQuery),
    );
  }, [vectorTiles, normalizedQuery]);

  // ==================== Render Helpers ====================

  const hasWmsLayers = wmsSettings?.wmsInfos && wmsSettings.wmsInfos.length > 0;
  const hasVectorTiles = vectorTiles && vectorTiles.length > 0;

  const getRadarActiveCount = useCallback(
    () => radarSources.filter((r) => r.enabled).length,
    [radarSources],
  );

  // Root level - show all groups directly (WMS services, DTN top-level groups, Vector Tiles)
  const renderRootGroups = () => {
    const items: React.ReactNode[] = [];

    // When searching, show matching layers directly
    if (normalizedQuery) {
      // Show matching WMS layers directly
      filteredWmsServices.forEach((wmsInfo) => {
        wmsInfo.layers?.forEach((layer) => {
          items.push(
            <LayerItem
              key={`wms-${layer.id}`}
              label={layer.title}
              sublabel={wmsInfo.name}
              isActive={doesLayerExist(layer.id)}
              isLoading={false}
              isFavorite={layerFavorites.isFavorite(layer.id)}
              isTogglingFavorite={layerFavorites.isToggling(layer.id)}
              onClick={() => handleWmsLayerSelect(wmsInfo, layer)}
              onFavoriteToggle={() =>
                layerFavorites.toggleFavorite(layer.id, 'wms', layer.title)
              }
            />,
          );
        });
      });

      // Show matching vector tiles directly
      filteredSearchVectorTiles.forEach((vt) => {
        items.push(
          <LayerItem
            key={`vt-${vt.id}`}
            label={vt.name}
            sublabel="Vector Tile"
            isActive={doesLayerExist(vt.id)}
            isLoading={false}
            isFavorite={layerFavorites.isFavorite(vt.id)}
            isTogglingFavorite={layerFavorites.isToggling(vt.id)}
            onClick={() => handleVectorTileSelect(vt)}
            onFavoriteToggle={() =>
              layerFavorites.toggleFavorite(vt.id, 'vectortile', vt.name)
            }
          />,
        );
      });

      // Show matching radar sources directly
      radarSources
        .filter((r) => r.name.toLowerCase().includes(normalizedQuery))
        .forEach((r) => {
          items.push(
            <LayerItem
              key={`radar-${r.id}`}
              label={r.name}
              sublabel="Radar Overlay"
              isActive={r.enabled}
              isLoading={false}
              onClick={() => toggleRadarLayer(r.id)}
              isFavorite={layerFavorites.isFavorite(r.id)}
              isTogglingFavorite={layerFavorites.isToggling(r.id)}
              onFavoriteToggle={() =>
                layerFavorites.toggleFavorite(r.id, 'cambridge-radar', r.name)
              }
            />,
          );
        });

      // Show matching ABB weather layers directly (requires permission)
      abbLayers
        .filter(
          (l) =>
            permissions.abbWeather &&
            l.label.toLowerCase().includes(normalizedQuery),
        )
        .forEach((layer) => {
          const group = abbGroups.find((g) => g.id === layer.group);
          items.push(
            <LayerItem
              key={`abb-${layer.title}`}
              label={layer.label}
              sublabel={group ? `Weather · ${group.label}` : 'Weather'}
              isActive={doesLayerExist(getABBMapLayerId(layer.title))}
              isLoading={abbLoadingLayerId === layer.title}
              onClick={() => toggleAbbLayer(layer)}
            />,
          );
        });

      // Show OpenSeaMap if it matches search
      if (
        'openseamap'.includes(normalizedQuery) ||
        'nautical seamark'.includes(normalizedQuery)
      ) {
        items.push(
          <ToggleItem
            key="openseamap"
            icon={<Anchor className="size-4" />}
            label="OpenSeaMap"
            isActive={doesLayerExist(OPENSEAMAP_LAYER_ID)}
            isFavorite={layerFavorites.isFavorite(LAYER_IDS.OPENSEAMAP)}
            isTogglingFavorite={layerFavorites.isToggling(LAYER_IDS.OPENSEAMAP)}
            onClick={handleOpenSeaMapToggle}
            onFavoriteToggle={() =>
              layerFavorites.toggleFavorite(
                LAYER_IDS.OPENSEAMAP,
                'openseamap',
                'OpenSeaMap',
              )
            }
          />,
        );
      }

      if (items.length === 0) {
        return (
          <div className="text-sm text-muted-foreground text-center py-4">
            No matching layers found
          </div>
        );
      }

      return <div className="flex flex-col gap-1">{items}</div>;
    }

    // Normal view - show groups
    // WMS Services
    if (hasWmsLayers) {
      wmsSettings.wmsInfos.forEach((wmsInfo) => {
        const activeCount = getWmsServiceActiveCount(wmsInfo);
        const favoriteCount = layerFavorites.getFavoriteCount(
          wmsInfo.layers || [],
        );
        items.push(
          <GroupItem
            key={`wms-${wmsInfo.id}`}
            icon={<Globe className="size-4" />}
            label={wmsInfo.name}
            count={wmsInfo.layers?.length || 0}
            activeCount={activeCount}
            favoriteCount={favoriteCount}
            onClick={() => navigateToGroup(wmsInfo.id, wmsInfo.name, 'wms')}
          />,
        );
      });
    }

    // Vector Tiles as a single group
    if (hasVectorTiles) {
      const activeCount = getVectorTileActiveCount();
      const favoriteCount = layerFavorites.getFavoriteCount(vectorTiles);
      items.push(
        <GroupItem
          key="vectortiles"
          icon={<Grid3X3 className="size-4" />}
          label="Vector Tiles"
          count={vectorTiles.length}
          activeCount={activeCount}
          favoriteCount={favoriteCount}
          onClick={() =>
            navigateToGroup('vectortiles', 'Vector Tiles', 'vectortile')
          }
        />,
      );
    }

    // Radar Overlays - only show if sources are configured in Settings
    if (radarSources.length > 0) {
      const radarFavoriteCount = layerFavorites.getFavoriteCount(radarSources);
      items.push(
        <GroupItem
          key="radar"
          icon={<Radio className="size-4" />}
          label="Radar Overlays"
          count={radarSources.length}
          activeCount={getRadarActiveCount()}
          favoriteCount={radarFavoriteCount}
          onClick={() => navigateToGroup('radar', 'Radar Overlays', 'radar')}
        />,
      );
    }

    // ABB Weather Groups (requires GHT-MDM-WEATHER_ABB permission)
    if (permissions.abbWeather && abbLayers.length > 0) {
      abbGroups.forEach((group) => {
        const layerCount = abbLayers.filter((l) => l.group === group.id).length;
        const activeCount = getAbbGroupActiveCount(group.id);
        items.push(
          <GroupItem
            key={`abb-${group.id}`}
            icon={<Cloud className="size-4" />}
            label={group.label}
            count={layerCount}
            activeCount={activeCount}
            onClick={() => navigateToGroup(group.id, group.label, 'abb')}
          />,
        );
      });
    }

    // OpenSeaMap - always available as a built-in overlay
    items.push(
      <ToggleItem
        key="openseamap"
        icon={<Anchor className="size-4" />}
        label="OpenSeaMap"
        isActive={doesLayerExist(OPENSEAMAP_LAYER_ID)}
        isFavorite={layerFavorites.isFavorite(LAYER_IDS.OPENSEAMAP)}
        isTogglingFavorite={layerFavorites.isToggling(LAYER_IDS.OPENSEAMAP)}
        onClick={handleOpenSeaMapToggle}
        onFavoriteToggle={() =>
          layerFavorites.toggleFavorite(
            LAYER_IDS.OPENSEAMAP,
            'openseamap',
            'OpenSeaMap',
          )
        }
      />,
    );

    if (items.length === 0) {
      return (
        <div className="text-sm text-muted-foreground text-center py-4">
          No external layers configured
        </div>
      );
    }

    return <div className="flex flex-col gap-1">{items}</div>;
  };

  // WMS layers within a service
  const renderWmsLayers = () => {
    const wmsInfo = wmsSettings.wmsInfos.find(
      (w) => w.id === navigation.groupId,
    );
    if (!wmsInfo) return null;

    const layers = normalizedQuery
      ? wmsInfo.layers?.filter((l) =>
          l.title.toLowerCase().includes(normalizedQuery),
        )
      : wmsInfo.layers;

    return (
      <div className="flex flex-col gap-1">
        {layers
          ?.sort((a, b) => a.title.localeCompare(b.title))
          .map((layer) => (
            <LayerItem
              key={layer.id}
              label={layer.title}
              isActive={doesLayerExist(layer.id)}
              isLoading={false}
              isFavorite={layerFavorites.isFavorite(layer.id)}
              isTogglingFavorite={layerFavorites.isToggling(layer.id)}
              onClick={() => handleWmsLayerSelect(wmsInfo, layer)}
              onFavoriteToggle={() =>
                layerFavorites.toggleFavorite(layer.id, 'wms', layer.title)
              }
            />
          ))}
        {(!layers || layers.length === 0) && (
          <div className="text-sm text-muted-foreground text-center py-4">
            No matching layers
          </div>
        )}
      </div>
    );
  };

  // Vector tiles (flat list)
  const renderVectorTiles = () => {
    const filtered = normalizedQuery
      ? vectorTiles.filter((vt) =>
          vt.name.toLowerCase().includes(normalizedQuery),
        )
      : vectorTiles;

    return (
      <div className="flex flex-col gap-1">
        {filtered.map((vt) => (
          <LayerItem
            key={vt.id}
            label={vt.name}
            isActive={doesLayerExist(vt.id)}
            isLoading={false}
            isFavorite={layerFavorites.isFavorite(vt.id)}
            isTogglingFavorite={layerFavorites.isToggling(vt.id)}
            onClick={() => handleVectorTileSelect(vt)}
            onFavoriteToggle={() =>
              layerFavorites.toggleFavorite(vt.id, 'vectortile', vt.name)
            }
          />
        ))}
        {filtered.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-4">
            No matching layers
          </div>
        )}
      </div>
    );
  };

  // Radar sources (flat list + add button)
  const renderRadarSources = () => {
    const filtered = normalizedQuery
      ? radarSources.filter((r) =>
          r.name.toLowerCase().includes(normalizedQuery),
        )
      : radarSources;

    return (
      <div className="flex flex-col gap-1">
        {filtered.map((r) => (
          <ToggleItem
            key={r.id}
            icon={<Radio className="size-4" />}
            label={r.name}
            isActive={r.enabled}
            isFavorite={layerFavorites.isFavorite(r.id)}
            isTogglingFavorite={layerFavorites.isToggling(r.id)}
            onClick={() => toggleRadarLayer(r.id)}
            onFavoriteToggle={() =>
              layerFavorites.toggleFavorite(r.id, 'cambridge-radar', r.name)
            }
          />
        ))}
        {filtered.length === 0 && radarSources.length > 0 && (
          <div className="text-sm text-muted-foreground text-center py-4">
            No matching radar sources
          </div>
        )}
        {radarSources.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-4">
            No radar sources configured. Add one in Settings → Radar.
          </div>
        )}
      </div>
    );
  };

  // ==================== ABB Group Content ====================
  const renderABBGroupContent = () => {
    const groupLayers = abbLayers.filter(
      (l) =>
        l.group === navigation.groupId &&
        (!normalizedQuery || l.label.toLowerCase().includes(normalizedQuery)),
    );

    if (isAbbLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    return (
      <div className="space-y-0.5">
        {groupLayers.map((layer) => (
          <LayerItem
            key={layer.title}
            label={layer.label}
            isActive={doesLayerExist(getABBMapLayerId(layer.title))}
            isLoading={abbLoadingLayerId === layer.title}
            onClick={() => toggleAbbLayer(layer)}
          />
        ))}
      </div>
    );
  };

  // Determine what to render based on navigation state
  const renderContent = () => {
    // Root level - show all groups
    if (!navigation.groupId) {
      return renderRootGroups();
    }

    // Inside a WMS service
    if (navigation.sourceType === 'wms') {
      return renderWmsLayers();
    }

    // Vector tiles
    if (navigation.sourceType === 'vectortile') {
      return renderVectorTiles();
    }

    // Radar overlays
    if (navigation.sourceType === 'radar') {
      return renderRadarSources();
    }

    // ABB Weather group
    if (navigation.sourceType === 'abb') {
      return renderABBGroupContent();
    }

    return null;
  };

  const currentTitle =
    navigation.breadcrumbs[navigation.breadcrumbs.length - 1]?.label ||
    'External Layers';
  const canGoBack = navigation.breadcrumbs.length > 1;

  // Calculate total active layers count
  const totalActiveCount = useMemo(() => {
    // Count active WMS layers
    const wmsCount = (wmsSettings?.wmsInfos || []).reduce(
      (acc: number, wms: WMSInfo) => acc + getWmsServiceActiveCount(wms),
      0,
    );

    // Count active Vector Tiles
    const vectorCount = getVectorTileActiveCount();

    // Count active OpenSeaMap layer
    const openSeaMapCount = doesLayerExist(OPENSEAMAP_LAYER_ID) ? 1 : 0;

    // Count active Radar layers
    const radarCount = getRadarActiveCount();

    // Count active ABB weather layers (only if permitted)
    const abbCount = permissions.abbWeather
      ? abbLayers.filter((l) => doesLayerExist(getABBMapLayerId(l.title)))
          .length
      : 0;

    return wmsCount + vectorCount + openSeaMapCount + radarCount + abbCount;
  }, [
    wmsSettings?.wmsInfos,
    getWmsServiceActiveCount,
    doesLayerExist,
    getVectorTileActiveCount,
    getRadarActiveCount,
    abbLayers,
    permissions.abbWeather,
  ]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              id="external-layers-button"
              size="icon"
              variant="outline"
              className="h-10 w-10 flex-shrink-0 flex-grow-0 relative"
            >
              <PiStackThin className="size-5" />
              {totalActiveCount > 0 && (
                <span className="absolute -top-1 -right-1.5 inline-flex items-center justify-center px-1 py-0.5 text-xs font-bold leading-none text-red-100 bg-primary rounded-full">
                  {totalActiveCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>External Layers</TooltipContent>
      </Tooltip>
      <PopoverContent className="w-80 p-0">
        {/* Header with back button and title */}
        <div className="flex items-center gap-2 px-3 pt-3 pb-1 border-b">
          {canGoBack && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={navigateBack}
            >
              <ChevronLeft className="size-4" />
            </Button>
          )}
          <h3 className="text-sm font-semibold flex-1 truncate">
            {currentTitle}
          </h3>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 text-foreground/70 hover:text-foreground"
              >
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={ROUTES.SETTINGS.WEB_MAP_SERVICES.$path()}>
                  <Globe className="size-4" />
                  WMS Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={ROUTES.SETTINGS.VECTOR_TILES.$path()}>
                  <Grid3X3 className="size-4" />
                  Vector Tile Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={ROUTES.SETTINGS.RADAR.$path()}>
                  <Radio className="size-4" />
                  Radar Settings
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Search */}
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <SearchInput
              placeholder="Search layers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery('')}
              className="pl-8 h-9"
              autoFocus
            />
          </div>
        </div>

        {/* Content */}
        <ScrollArea className="h-72">
          <div className="p-2 overflow-hidden">{renderContent()}</div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

// ==================== Sub-components ====================

interface GroupItemProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  activeCount: number;
  favoriteCount?: number;
  onClick: () => void;
}

const GroupItem: React.FC<GroupItemProps> = ({
  icon,
  label,
  count,
  activeCount,
  favoriteCount,
  onClick,
}) => (
  <button
    className={cn(
      'flex items-center gap-3 px-3 py-2 rounded-md text-left overflow-hidden',
      'hover:bg-accent hover:text-accent-foreground transition-colors ease-out',
    )}
    onClick={onClick}
  >
    <span className="shrink-0 text-muted-foreground">{icon}</span>
    <span className="w-0 flex-grow text-sm font-medium truncate">{label}</span>
    {(favoriteCount ?? 0) > 0 && (
      <span className="flex items-center gap-1">
        <PiStarFill className="size-3 text-yellow-400" />
        <span className="text-xs text-yellow-500 font-medium">
          {favoriteCount}
        </span>
      </span>
    )}
    {activeCount > 0 && (
      <span className="flex items-center gap-1">
        <Circle className="size-2 fill-primary text-primary" />
        <span className="text-xs text-primary font-medium">{activeCount}</span>
      </span>
    )}
    <span className="text-xs text-muted-foreground">{count}</span>
    <ChevronRight className="size-4 text-muted-foreground" />
  </button>
);

interface LayerItemProps {
  label: string;
  sublabel?: string;
  isActive: boolean;
  isLoading: boolean;
  isFavorite?: boolean;
  isTogglingFavorite?: boolean;
  onClick: () => void;
  onFavoriteToggle?: () => void;
}

const LayerItem: React.FC<LayerItemProps> = ({
  label,
  sublabel,
  isActive,
  isLoading,
  isFavorite,
  isTogglingFavorite,
  onClick,
  onFavoriteToggle,
}) => (
  <div
    className={cn(
      'flex items-center gap-2 px-3 py-2 rounded-md text-left overflow-hidden',
      isActive && 'bg-secondary',
    )}
  >
    <div className="w-0 flex-grow overflow-hidden">
      <TruncatedText
        text={label}
        className={cn('text-sm', isActive && 'font-semibold')}
        maxWidth="100%"
      />
      {sublabel && (
        <div className="text-xs text-muted-foreground truncate">{sublabel}</div>
      )}
    </div>
    {onFavoriteToggle && (
      <button
        className="shrink-0 p-1 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors ease-out"
        disabled={isTogglingFavorite}
        onClick={(e) => {
          e.stopPropagation();
          onFavoriteToggle();
        }}
        title={isFavorite ? 'Remove from startup' : 'Show on startup'}
      >
        {isTogglingFavorite ? (
          <Icons.spinner className="size-4 animate-spin" />
        ) : isFavorite ? (
          <PiStarFill className="size-4 text-yellow-400" />
        ) : (
          <PiStar className="size-4 text-muted-foreground" />
        )}
      </button>
    )}
    <button
      className="shrink-0 p-1 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors ease-out"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={isLoading}
      title={isActive ? 'Hide layer' : 'Show layer'}
    >
      {isLoading ? (
        <Icons.spinner className="size-4 animate-spin" />
      ) : isActive ? (
        <PiEye className="size-4 text-primary" />
      ) : (
        <PiEyeClosed className="size-4 text-muted-foreground" />
      )}
    </button>
  </div>
);

interface ToggleItemProps {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  isFavorite?: boolean;
  isTogglingFavorite?: boolean;
  onClick: () => void;
  onFavoriteToggle?: () => void;
}

const ToggleItem: React.FC<ToggleItemProps> = ({
  icon,
  label,
  isActive,
  isFavorite,
  isTogglingFavorite,
  onClick,
  onFavoriteToggle,
}) => (
  <div
    className={cn(
      'flex items-center gap-3 px-3 py-2 rounded-md text-left overflow-hidden',
      isActive && 'bg-secondary',
    )}
  >
    <span className="shrink-0 text-muted-foreground">{icon}</span>
    <span
      className={cn(
        'w-0 flex-grow text-sm truncate',
        isActive ? 'font-semibold' : 'font-medium',
      )}
    >
      {label}
    </span>
    {onFavoriteToggle && (
      <button
        className="shrink-0 p-1 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors ease-out"
        disabled={isTogglingFavorite}
        onClick={(e) => {
          e.stopPropagation();
          onFavoriteToggle();
        }}
        title={isFavorite ? 'Remove from startup' : 'Show on startup'}
      >
        {isTogglingFavorite ? (
          <Icons.spinner className="size-4 animate-spin" />
        ) : isFavorite ? (
          <PiStarFill className="size-4 text-yellow-400" />
        ) : (
          <PiStar className="size-4 text-muted-foreground" />
        )}
      </button>
    )}
    <button
      className="shrink-0 p-1 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors ease-out"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={isActive ? 'Hide layer' : 'Show layer'}
    >
      {isActive ? (
        <PiEye className="size-4 text-primary" />
      ) : (
        <PiEyeClosed className="size-4 text-muted-foreground" />
      )}
    </button>
  </div>
);

export default ExternalLayers;
